<template>
  <v-dialog
    v-model="dialog"
    max-width="600"
    persistent
  >
    <v-card>
      <v-card-title class="headline primary white--text">
        <v-icon left color="white">mdi-gas-station-plus</v-icon>
        {{ $t('Augmentation ponctuelle') }}
      </v-card-title>

      <v-card-text class="pt-4">
        <v-alert
          type="warning"
          dense
          outlined
          class="mb-4"
        >
          <div class="font-weight-bold mb-2">
            ⚠️ {{ $t('Attention : Augmentation temporaire') }}
          </div>
          <div class="text-caption">
            {{ $t('Cette augmentation est exceptionnelle et temporaire. Elle sera automatiquement annulée lors du prochain renouvellement automatique du bon.') }}
          </div>
          <div class="text-caption mt-2">
            {{ $t('Fréquence de renouvellement') }}: <strong>{{ frequencyLabel }}</strong>
          </div>
        </v-alert>

        <v-form ref="form" v-model="valid">
          <!-- Informations actuelles -->
          <v-card outlined class="mb-4 pa-3 grey lighten-4">
            <div class="text-subtitle-2 mb-2">{{ $t('Valeurs actuelles') }}</div>
            <v-row dense>
              <v-col cols="6">
                <div class="text-caption grey--text">{{ $t('Litres') }}</div>
                <div class="text-h6">{{ formatNumber(voucher.purchase_liters) }} L</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text">{{ $t('Montant') }}</div>
                <div class="text-h6">{{ formatNumber(voucher.purchase_amount) }} XOF</div>
              </v-col>
            </v-row>
          </v-card>

          <!-- Litres supplémentaires -->
          <v-text-field
            v-model.number="form.additional_liters"
            :rules="rules.additional_liters"
            type="number"
            step="0.01"
            min="0.01"
            outlined
            dense
            :label="$t('Litres supplémentaires') + ' *'"
            prefix="+"
            suffix="L"
            @input="calculateAdditionalAmount"
          >
            <template v-slot:append-outer>
              <v-icon color="primary">mdi-gas-station</v-icon>
            </template>
          </v-text-field>

          <!-- Montant calculé automatiquement -->
          <v-text-field
            :value="additionalAmountDisplay"
            outlined
            dense
            readonly
            :label="$t('Montant supplémentaire calculé')"
            prefix="+"
            suffix="XOF"
          >
            <template v-slot:append-outer>
              <v-icon color="success">mdi-cash-multiple</v-icon>
            </template>
          </v-text-field>

          <!-- Prix unitaire (info) -->
          <v-alert
            dense
            text
            color="secondary"
            class="mb-4"
          >
            {{ $t('Prix unitaire') }}: {{ formatNumber(productPrice) }} XOF/L
          </v-alert>

          <!-- Motif -->
          <v-textarea
            v-model="form.reason"
            outlined
            dense
            rows="3"
            :label="$t('Motif (optionnel)')"
            :placeholder="$t('Exemple: Besoin exceptionnel pour trajet longue distance')"
            counter="500"
            maxlength="500"
          ></v-textarea>

          <!-- Aperçu des nouvelles valeurs -->
          <v-card outlined class="pa-3 primary lighten-5">
            <div class="text-subtitle-2 mb-2 primary--text">
              {{ $t('Nouvelles valeurs après augmentation') }}
            </div>
            <v-row dense>
              <v-col cols="6">
                <div class="text-caption grey--text">{{ $t('Litres totaux') }}</div>
                <div class="text-h6 primary--text">
                  {{ formatNumber(newTotalLiters) }} L
                  <span class="text-caption success--text">(+{{ formatNumber(form.additional_liters || 0) }})</span>
                </div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text">{{ $t('Montant total') }}</div>
                <div class="text-h6 primary--text">
                  {{ formatNumber(newTotalAmount) }} XOF
                  <span class="text-caption success--text">(+{{ formatNumber(additionalAmount) }})</span>
                </div>
              </v-col>
            </v-row>
          </v-card>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          text
          @click="close"
          :disabled="loading"
        >
          {{ $t('commons.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="!valid || loading"
          @click="submit"
        >
          <v-icon left>mdi-check</v-icon>
          {{ $t('Appliquer l\'augmentation') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { required, isNumber } from "@/utils/validator"
import { FREQUENCY_ITEMS } from "@/utils/consts"
import CompanyVouchersService from "@/services/CompanyVouchersService"

export default {
  name: "ExceptionalIncreaseDialog",
  props: {
    value: {
      type: Boolean,
      default: false
    },
    voucher: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      valid: false,
      loading: false,
      form: {
        additional_liters: null,
        reason: ''
      },
      rules: {
        additional_liters: [
          required(this.$t('Le nombre de litres est obligatoire')),
          isNumber,
          v => (v && v > 0) || this.$t('La valeur doit être positive')
        ]
      }
    }
  },
  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    productPrice() {
      return this.voucher?.company_product?.price || 0
    },
    additionalAmount() {
      const liters = Number(this.form.additional_liters) || 0
      return liters * this.productPrice
    },
    additionalAmountDisplay() {
      return this.formatNumber(this.additionalAmount)
    },
    newTotalLiters() {
      const current = Number(this.voucher?.purchase_liters) || 0
      const additional = Number(this.form.additional_liters) || 0
      return current + additional
    },
    newTotalAmount() {
      const current = Number(this.voucher?.purchase_amount) || 0
      return current + this.additionalAmount
    },
    frequencyLabel() {
      const freq = this.voucher?.frequency || 'Single'
      const item = FREQUENCY_ITEMS.find(f => 
        f.value === freq || f.text === freq || f === freq
      )
      return item?.text || freq
    }
  },
  methods: {
    formatNumber(value) {
      if (value === null || value === undefined || value === "") return "0"
      const n = Number(value)
      if (Number.isNaN(n)) return String(value)
      return n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    calculateAdditionalAmount() {
      // Le calcul se fait automatiquement via computed property
      // Cette méthode peut servir pour des validations supplémentaires si besoin
    },
    async submit() {
      if (!this.$refs.form.validate()) return
      
      this.loading = true
      try {
        const response = await CompanyVouchersService.exceptionalIncrease(
          this,
          this.voucher.id,
          {
            additional_liters: this.form.additional_liters,
            reason: this.form.reason
          }
        )
        
        this.$store.commit("app/message", {
          msg: response.message || this.$t('Augmentation appliquée avec succès'),
          color: "success"
        })
        
        // Afficher l'avertissement
        if (response.warning) {
          setTimeout(() => {
            this.$store.commit("app/message", {
              msg: response.warning,
              color: "warning"
            })
          }, 2000)
        }
        
        this.$emit('success', response.voucher)
        this.close()
      } catch (err) {
        let message = this.$t('Une erreur est survenue')
        if (err.response?.data?.error) {
          message = err.response.data.error
        } else if (err.response?.data) {
          message = Object.values(err.response.data).join(', ')
        }
        this.$store.commit("app/message", {
          msg: message,
          color: "error"
        })
      } finally {
        this.loading = false
      }
    },
    close() {
      this.$refs.form.reset()
      this.form = {
        additional_liters: null,
        reason: ''
      }
      this.dialog = false
    }
  }
}
</script>